import SunCalc from 'suncalc3'

export interface SunPosition {
  azimuth: number // 0-360, North = 0, East = 90
  elevation: number // -90 to 90, negative = below horizon
}

export interface SolarConfig {
  latitude: number
  longitude: number
}

// The date the CSV telemetry data represents
export const SIMULATION_DATE = '2026-01-02'

// Delhi, India coordinates (28°34'43"N 77°15'37"E)
export const DELHI_LOCATION: SolarConfig = {
  latitude: 28.5786,
  longitude: 77.2606,
}

/**
 * Calculate sun position for a given date/time and location
 * Converts suncalc's South=0 convention to North=0
 */
export function getSunPosition(date: Date, config: SolarConfig = DELHI_LOCATION): SunPosition {
  const pos = SunCalc.getPosition(date, config.latitude, config.longitude)

  // Convert azimuth: suncalc uses South=0, we use North=0
  // Add 180 to shift from South-based to North-based
  let azimuth = (pos.azimuthDegrees + 180) % 360
  if (azimuth < 0) azimuth += 360

  return {
    azimuth,
    elevation: pos.altitudeDegrees,
  }
}

/**
 * Create a date from a date string and time (hours since midnight)
 */
export function createDateWithTime(dateStr: string, timeHours: number): Date {
  const date = new Date(dateStr + 'T00:00:00')
  const hours = Math.floor(timeHours)
  const minutes = Math.floor((timeHours - hours) * 60)
  const seconds = Math.floor(((timeHours - hours) * 60 - minutes) * 60)

  date.setHours(hours, minutes, seconds, 0)
  return date
}

/**
 * Get current time in Delhi timezone as hours since midnight
 * Delhi is UTC+5:30
 */
export function getDelhiCurrentTime(): { dateStr: string; timeHours: number } {
  const now = new Date()
  // Delhi is UTC+5:30 (5.5 hours ahead of UTC)
  const delhiOffset = 5.5 * 60 * 60 * 1000
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60 * 1000
  const delhiDate = new Date(utcTime + delhiOffset)

  const year = delhiDate.getFullYear()
  const month = String(delhiDate.getMonth() + 1).padStart(2, '0')
  const day = String(delhiDate.getDate()).padStart(2, '0')

  return {
    dateStr: `${year}-${month}-${day}`,
    timeHours: delhiDate.getHours() + delhiDate.getMinutes() / 60 + delhiDate.getSeconds() / 3600,
  }
}

/**
 * Format time hours to HH:MM string
 */
export function formatTimeHours(timeHours: number): string {
  const hours = Math.floor(timeHours)
  const minutes = Math.floor((timeHours - hours) * 60)
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Get compass direction from azimuth
 */
export function getCompassDirection(azimuth: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  const index = Math.round(azimuth / 45) % 8
  return directions[index]
}
